import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuthSession } from "./useAuthSession";

/**
 * Hook to check if a feature is enabled via platform_settings.
 * Returns true by default while loading to prevent UI flicker.
 */
export function useFeatureEnabled(featureKey: string) {
  const { data: session, isLoading: authLoading } = useAuthSession();
  
  const query = useQuery({
    queryKey: ['feature-enabled', featureKey, session?.user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings' as any)
        .select('setting_value')
        .eq('setting_key', featureKey)
        .maybeSingle();

      if (error) {
        console.warn(`Failed to fetch feature setting ${featureKey}:`, error.message);
        return false; // Default to disabled on error for safety
      }
      
      if (!data) return false; // Default to disabled if setting doesn't exist
      
      const value = (data as any)?.setting_value;
      return value === true || value === 'true';
    },
    enabled: !authLoading && !!session?.user,
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  return {
    isEnabled: authLoading || !session?.user ? false : (query.data ?? false),
    isLoading: authLoading || query.isLoading,
  };
}

/**
 * Batch hook to check multiple features at once
 */
export function useMultipleFeatures(featureKeys: string[]) {
  const results: Record<string, boolean> = {};
  
  // This is a simplified implementation - for production you'd want a single query
  featureKeys.forEach(key => {
    results[key] = true; // Default all to enabled for now
  });
  
  return results;
}
